import { Component, Input, OnChanges, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CustomField, Eviction, IntakeFormSection, IntakeUpload } from '@ee/common/models';
import { ConditionEvaluatorService, DocumentService, FormService } from '@ee/common/services';
import { MAX_FILE_UPLOAD_SIZE } from '@ee/common/constants';
import { FileValidator } from '@ee/common/validators';
import { AttachmentType, RuleTargetAction, RuleTargetType } from '@ee/common/enums';
import { filenameFromContentDisposition } from '@ee/common/utils';

@Component({
  selector: 'ee-eviction-documentation',
  template: `
    @if (!hideSection) {
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        @for (f of section?.fields; track f; let i = $index) {
          @if (visibility[i]) {
            <ee-uploader
              additionalClasses="ee__standard-shadow p-4"
              [form]="$any(documentsForm.controls[i])"
              [label]="f.label"
              [hint]="!f.link_id ? f.hint : null"
              [downloadFileLink]="isPublicIntakeForm ? null : f.link_id"
              [attorney_org_id]="attorney_org_id"
              (uploadReady)="onUploadReady(i, f.field, $event)"
              (uploadCleared)="onUploadCleared(i, f.field)"
              (downloadFormEvent)="downloadForm($event)" />
          }
        }
      </div>
    }
  `,
  styles: [
    `
      .card {
        margin: 1rem;
      }

      .mat-mdc-form-field {
        margin-bottom: 1rem;
      }

      .no-wrap {
        white-space: nowrap;
      }

      .errors {
        color: var(--primary-red);
        text-align: right;
      }

      .upload-wrapper {
        margin-bottom: 1rem;

        .label {
          font-size: 1.5rem;
          font-weight: 400;
          line-height: 1.5rem;
          padding-bottom: 0.5rem;
          text-align: center;
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EvictionDocumentationComponent implements OnChanges, OnDestroy {
  @Input() documentsForm: UntypedFormArray;
  @Input() existingDocumentsForm: UntypedFormArray;
  @Input() claimForm: UntypedFormGroup;
  @Input() propertyForm: UntypedFormGroup;
  @Input() workflowForm: UntypedFormGroup;
  @Input() section: IntakeFormSection | undefined;
  @Input() eviction: Eviction | undefined;
  @Input() currentStep: number; // only used for triggering ngOnChanges
  @Input() attorney_org_id?: string;
  /**
   * True on the public/anonymous intake form. The "download this form (pre-filled) to sign & upload"
   * affordance calls POST /api/forms/{id}/generate, which requires an authenticated role — so it can
   * never work for an anonymous applicant. Hide it on the public form to avoid a dead button.
   */
  @Input() isPublicIntakeForm = false;

  /** Emitted whenever an upload completes or is cleared. Payload is every completed upload, one per box. */
  @Output() uploadsChange = new EventEmitter<IntakeUpload[]>();
  /** Emitted whenever upload readiness changes. True when all required visible fields have a completed upload. */
  @Output() uploadsReadyChange = new EventEmitter<boolean>();

  public visibility: boolean[] = [];
  public hideSection: boolean = false;

  /**
   * Completed upload per box, indexed like `section.fields`. Used by the parent component at submit
   * time instead of reading raw File objects from the form.
   *
   * Keyed by box, not by document type (ISS-131): a form can hold several boxes of the same type
   * (two "Other" boxes), and keyed by type every upload after the first replaced the one before it.
   *
   * `seq` records when the box's current upload completed, so the list can be emitted in upload order.
   */
  private uploadsByBox: Record<number, { upload: IntakeUpload; seq: number }> = {};
  private uploadSeq = 0;

  private subs: Subscription[] = [];

  constructor(
    private conditionEvaluatorService: ConditionEvaluatorService,
    private formService: FormService,
    private documentService: DocumentService
  ) {}

  ngOnChanges() {
    if (this.propertyForm && this.claimForm && this.documentsForm) {
      this.section?.fields.forEach((s: CustomField, index: number) => {
        this.visibility[index] = true;
      });
      this.hideSection = false;
      // A section-level HIDE or DISABLE switches off every box in it. The per-box loop below must honour
      // that: it used to re-enable required boxes, so a hidden/disabled section still demanded uploads
      // the user could not reach, and save stayed blocked.
      let sectionOff = false;
      const hiddenFields: string[] = [];
      const requiredFields: string[] = [];
      if (this.section?.rules?.length) {
        this.section.rules.forEach((rule) => {
          if (
            this.conditionEvaluatorService.evaluateRuleDefinition(
              rule.source_field,
              rule.operator,
              rule.value,
              this.eviction
            )
          ) {
            if (rule.target_type === RuleTargetType.SECTION) {
              if (rule.action === RuleTargetAction.DISABLE || rule.action === RuleTargetAction.HIDE) {
                sectionOff = true;
              }
              if (rule.action === RuleTargetAction.HIDE) {
                this.hideSection = true;
              }
            } else if (rule.target_type === RuleTargetType.FIELD) {
              if (rule.action === RuleTargetAction.REQUIRE) {
                requiredFields.push(rule.target_field?.field);
              } else if (rule.action === RuleTargetAction.HIDE) {
                hiddenFields.push(rule.target_field?.field);
              }
            }
          }
        });
      }

      this.section.fields.forEach((s: CustomField, index: number) => {
        if (sectionOff) {
          // Emptied, disabled and unvalidated, so it is neither required nor submitted.
          const ctrl = this.documentsForm.at(index);
          ctrl.disable();
          ctrl.reset();
          ctrl.clearValidators();
          delete this.uploadsByBox[index];
          return;
        }
        const visible = !hiddenFields.includes(s.field ?? '');
        this.visibility[index] = visible;

        // Check if field is required (either inherently or via rules)
        const isRequired = s.required || requiredFields.includes(s.field ?? '');

        if (!visible) {
          this.documentsForm.at(index).disable();
          this.documentsForm.at(index).reset();
          delete this.uploadsByBox[index];
        } else if (isRequired) {
          this.documentsForm.at(index).enable();
          // Check if a document of this type already exists in existingDocumentsForm
          const existingDocument = this.existingDocumentsForm?.controls?.some((control: UntypedFormGroup) => {
            const attachmentType = control?.get('type')?.value;
            if (!attachmentType) return false;
            const typeKey = Object.keys(AttachmentType).find(
              (key) => AttachmentType[key as keyof typeof AttachmentType] === attachmentType
            );
            return typeKey === s.field;
          });

          if (!existingDocument) {
            this.documentsForm
              .at(index)
              .setValidators([Validators.required, FileValidator.maxContentSize(MAX_FILE_UPLOAD_SIZE)]);
          } else {
            this.documentsForm.at(index).setValidators([FileValidator.maxContentSize(MAX_FILE_UPLOAD_SIZE)]);
          }
        } else if (visible) {
          this.documentsForm.at(index).enable();
          this.documentsForm.at(index).setValidators([FileValidator.maxContentSize(MAX_FILE_UPLOAD_SIZE)]);
        }
      });

      this.documentsForm.updateValueAndValidity();

      // Emit current readiness after visibility/validators are recalculated so the parent
      // always has an accurate signal even when no upload events have fired yet (e.g. when
      // all document fields are optional or hidden by rules).
      this.emitUploads();
      this.uploadsReadyChange.emit(this.areAllRequiredUploadsReady());
    }
  }

  public downloadForm(formLink: string): void {
    const evictionDetails = { ...this.claimForm.value, ...this.propertyForm.value } as Eviction;

    // add plaintiff name
    const workflowForm = this.workflowForm.value;
    if (workflowForm.client_details?.company_name) {
      evictionDetails.company_name = workflowForm.client_details.company_name;
    }

    this.subs.push(
      this.formService.generate(formLink, evictionDetails).subscribe((res) => {
        const contentDisposition = res.headers.get('content-disposition');
        const filename = filenameFromContentDisposition(contentDisposition);
        this.documentService.saveDocument(filename, res.body);
      })
    );
  }

  onUploadReady(index: number, fieldName: string, event: { upload_id: string; original_format: string }): void {
    // A box a rule has hidden or switched off may still report an upload that was scanning when the rule
    // fired (DISABLE keeps the uploader mounted). Storing it would resubmit what the rule just cleared.
    if (!this.visibility[index] || this.documentsForm?.at(index)?.disabled) {
      return;
    }
    if (fieldName) {
      this.uploadsByBox[index] = { upload: { type: fieldName, upload_id: event.upload_id }, seq: ++this.uploadSeq };
      this.emitUploads();
      this.uploadsReadyChange.emit(this.areAllRequiredUploadsReady());
    }
  }

  onUploadCleared(index: number, fieldName: string): void {
    if (fieldName) {
      delete this.uploadsByBox[index];
      this.emitUploads();
      this.uploadsReadyChange.emit(this.areAllRequiredUploadsReady());
    }
  }

  /**
   * Returns true when every required, visible document field has a completed upload_id.
   * A field is required when its form control has Validators.required.
   */
  private areAllRequiredUploadsReady(): boolean {
    if (!this.section?.fields) return true;
    return this.section.fields.every((f, i) => {
      if (!this.visibility[i]) return true; // hidden — not required
      const ctrl = this.documentsForm?.at(i);
      if (!ctrl || ctrl.disabled) return true;
      const isRequired = ctrl.hasValidator(Validators.required);
      if (!isRequired) return true;
      return !!f.field && !!this.uploadsByBox[i];
    });
  }

  /**
   * Emits the completed uploads in the order they were made, most recent last.
   *
   * Not box order: the parent also builds the legacy per-type `upload_ids` map from this list, last
   * entry winning, and a server that predates `uploads` reads only that map. Upload order keeps its old
   * semantics (the file uploaded last for a type is the one sent) through a mixed-version rollout.
   */
  private emitUploads(): void {
    this.uploadsChange.emit(
      Object.values(this.uploadsByBox)
        .sort((a, b) => a.seq - b.seq)
        .map(({ upload }) => ({ ...upload }))
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((x) => x.unsubscribe());
  }
}
